import { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import LanguageSwitcher from '../../Ui/LanguageSwitcher';
import { useDoctorContext } from '../../../context/DoctorContext';

export default function HeaderSection() {
    const { t, isRTL } = useLanguage();

    const [menuOpen,   setMenuOpen]   = useState(false);
    const [activeLink, setActiveLink] = useState('home');
    const [scrolled,   setScrolled]   = useState(false);

    const navLinks = [
        { key: 'home',          label: t('comingSoon.nav.home')          || 'Home',          href: '#about'         },
        { key: 'features',      label: t('comingSoon.nav.features')      || 'Features',      href: '#features'      },
        { key: 'ourApp',        label: t('comingSoon.nav.ourApp')        || 'Our App',       href: '#ourApp'        },
        { key: 'revolutionary', label: t('comingSoon.nav.revolutionary') || 'Revolutionary', href: '#revolutionary' },
        { key: 'plans',         label: t('comingSoon.nav.plans')         || 'Plans',         href: '#plans'         },
        { key: 'contact',       label: t('header.nav.contact'),                              href: '#contact'       },
    ];


    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const handleNav = (key, href) => {
        setActiveLink(key);
        setMenuOpen(false);
        if (href.startsWith('#') && href.length > 1) {
            const el = document.getElementById(href.slice(1));
            if (el) {
                const y = el.getBoundingClientRect().top + window.scrollY - 80;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        }
    };

    return (
        <header
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`sticky top-0 z-50 w-full bg-[#f7fcfd] border-b border-gray-200 transition-shadow duration-300 md:py-5${
                scrolled ? 'shadow-md' : 'shadow-sm'
            }`}
        >
            {/* ───── Desktop bar ───── */}
            <div className="max-w-[1200px] mx-auto px-4 xl:px-8 py-2 md:py-3 flex items-center justify-between gap-4">

                {/* ── Brand ── */}
                <a href="/" className="flex items-center gap-2.5 no-underline flex-shrink-0">
                 
                    <div>
                        <p className="md:text-3xl font-bold ">
                            <img src='/images/headerLogo.png' className='w-32 h-auto' />
                        </p>
                        {/* <p className="text-[10px] text-gray-400 uppercase tracking-[0.5px] mt-0.5">
                            {t('comingSoon.nav.platformTagline') || 'Healthcare Platform'}
                        </p> */}
                    </div>
                </a>

                {/* ── Desktop Nav ── */}
                <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
                    {navLinks.map(({ key, label, href }) => (
                        <button
                            key={key}
                            onClick={() => handleNav(key, href)}
                            className={`
                                relative px-2.5 py-1.5 text-[15px] font-medium whitespace-nowrap
                                bg-transparent border-none cursor-pointer group
                                transition-colors duration-200
                                ${activeLink === key
                                    ? 'text-secondary font-semibold'
                                    : 'text-gray-600 hover:text-secondary'
                                }
                            `}
                        >
                            {label}
                            {/* Underline slide-in — matches your existing header */}
                            <span
                                className={`
                                    absolute bottom-0 h-0.5 bg-secondary rounded-full
                                    transition-all duration-300
                                    ${isRTL ? 'right-0' : 'left-0'}
                                    ${activeLink === key ? 'w-full' : 'w-0 group-hover:w-full'}
                                `}
                            />
                        </button>
                    ))}
                </nav>

                {/* ── Right: Language + Status + Hamburger ── */}
                <div className="flex items-center gap-3 flex-shrink-0">

                    {/* Language switcher — desktop (same as your main header) */}
                    <span className="hidden md:block">
                        <LanguageSwitcher />
                    </span>


                    {/* Hamburger — mobile */}
                    <button
                        onClick={() => setMenuOpen(o => !o)}
                        aria-label="Toggle menu"
                        className="md:hidden flex flex-col items-center justify-center gap-[5px] w-[38px] h-[38px] rounded-lg border border-gray-200 bg-white cursor-pointer flex-shrink-0"
                    >
                        <span
                            className="block w-5 h-0.5 bg-gray-500 rounded-full transition-transform duration-250"
                            style={{ transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }}
                        />
                        <span
                            className="block w-5 h-0.5 bg-gray-500 rounded-full transition-opacity duration-200"
                            style={{ opacity: menuOpen ? 0 : 1 }}
                        />
                        <span
                            className="block w-5 h-0.5 bg-gray-500 rounded-full transition-transform duration-250"
                            style={{ transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }}
                        />
                    </button>
                </div>
            </div>

            {/* ───── Mobile Drawer ───── */}
            {menuOpen && (
                <div className="md:hidden border-t border-gray-200 bg-[#f7fcfd] px-4 pt-3 pb-5">

                    {/* Language switcher row — mobile */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                            {isRTL ? 'اللغة' : 'Language'}
                        </span>
                        <LanguageSwitcher />
                    </div>

                    <nav className="flex flex-col gap-1 mb-3">
                        {navLinks.map(({ key, label, href }) => (
                            <button
                                key={key}
                                onClick={() => handleNav(key, href)}
                                className={`
                                    w-full text-${isRTL ? 'right' : 'left'} px-3.5 py-2.5 text-[15px] font-medium
                                    rounded-lg border-none cursor-pointer transition-colors duration-200
                                    ${activeLink === key
                                        ? 'bg-secondary/10 text-secondary'
                                        : 'bg-transparent text-gray-700 hover:bg-secondary/10 hover:text-secondary'
                                    }
                                `}
                            >
                                {label}
                            </button>
                        ))}
                    </nav>

                    {/* Status row */}
                    <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-2">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        {t('comingSoon.title') || 'Coming Soon'}
                    </div>
                </div>
            )}
        </header>
    );
}